import React, { useCallback, useEffect, useRef, useState } from "react";
import { CrewPilotModuleShell } from "./CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../layout/pageLayout";
import { CreateClientScreen } from "./CreateClientScreen";
import { EstimateSummaryScreen } from "./EstimateSummaryScreen";
import { EstimateWorkspaceScreen } from "./estimate/EstimateWorkspaceScreen";
import { LocationWorkspaceScreen } from "./estimate/LocationWorkspaceScreen";
import { FeatureWorkspaceScreen } from "./estimate/FeatureWorkspaceScreen";
import { DesktopEstimateEditor } from "./estimate/desktop/editor/DesktopEstimateEditor";
import { EstimateReviewView } from "./estimate/review/EstimateReviewView";
import { CPInput } from "./CPInput";
import { CPDropdown } from "./CPDropdown";
import {
  CP_MODAL_OVERLAY_STYLE,
  CP_MODAL_PANEL_STYLE,
} from "./shared/crewpilotModalStyles";
import { ViewportPortal } from "./shared/ViewportDialog";
import {
  HUB_STATUS_PILL_ACTIVE_GOLD_STYLE,
  HUB_STATUS_PILL_BTN_BASE_STYLE,
  HUB_STATUS_PILLS_WRAP_STYLE,
} from "./shared/hubFilterPillStyles";
import "./EstimateCreatePage.css";
import "./EstimateHeaderWorkspace.css";
import "./EstimateHubLanding.css";
import { supabase } from "../supabase";
import { blockDemoSave, shouldSkipSupabase } from "../helpers/guestMode";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import {
  archiveEstimate,
  createEstimate,
  ensureMvpEstimateCatalog,
  ESTIMATE_STATUS_OPTIONS,
  ESTIMATE_CREATE_DIAGNOSTIC_CODES,
  formatEstimateStatus,
  formatLocationLabel,
  formatSupabaseError,
  generateEstimateNumber,
  isEstimateEditable,
  loadClientsForEstimates,
  loadEstimateTree,
  loadEstimates,
  loadFeatureTypesCatalog,
  loadMaterialsCatalog,
  resolveEstimateStatus,
  updateEstimateHeader,
  updateEstimatePricing,
  updateEstimateStatus,
  ESTIMATE_ACTIVE_HUB_STATUSES,
} from "../helpers/estimates";
import {
  ESTIMATE_BUILDER_MODES,
  getBuilderShellTitle,
  isEstimateBuilderEditable,
} from "../helpers/estimateWorkflow";
import { getEstimateStatusPillStyle } from "../helpers/estimateStatusUi";
import { DEFAULT_ESTIMATE_REPORT_OPTIONS } from "../helpers/estimateReport";
import { loadWorkTypeTemplates } from "../helpers/workTypeTemplates";
import { EstimateReportOptionsScreen } from "./estimate/report/EstimateReportOptionsScreen";
import { EstimateReportPreviewScreen } from "./estimate/report/EstimateReportPreviewScreen";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY } from "../helpers/permissions";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { DeskPanel } from "./desktop/DeskTable";
import "./estimate/desktop/estimateDesktop.css";
import { EstimateHubDesktop } from "./estimate/desktop/EstimateHubDesktop";
import { HubPrimaryAction } from "./shared/HubPrimaryAction";

function formatEstimateCardDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const HUB_QUICK_ARCHIVE_STATUSES = new Set(["approved", "converted", "declined"]);

const MODAL_OVERLAY_STYLE = CP_MODAL_OVERLAY_STYLE;
const MODAL_PANEL_STYLE = CP_MODAL_PANEL_STYLE;

function EstimateHubCard({ estimate, onOpen, onArchive, archiving }) {
  const status = resolveEstimateStatus(estimate.status);
  const statusLabel = formatEstimateStatus(status);
  const pillStyle = getEstimateStatusPillStyle(status);
  const clientName = estimate.clients?.client_name || "—";
  const updatedLabel = formatEstimateCardDate(estimate.updated_at);
  const showArchivePill = HUB_QUICK_ARCHIVE_STATUSES.has(status);

  return (
    <div className="estimate-hub-card">
      <button
        type="button"
        className="estimate-hub-card__open"
        onClick={() => onOpen(estimate.estimate_id)}
      >
        <div className="estimate-hub-card__identity">
          <div className="estimate-hub-card__title">
            {estimate.title || "Untitled Estimate"}
          </div>
          <div className="estimate-hub-card__id">{estimate.estimate_number}</div>
          <div className="estimate-hub-card__client">Client: {clientName}</div>
        </div>

        <div className="estimate-hub-card__status">
          <div className="estimate-hub-card__status-pill" style={pillStyle}>
            {statusLabel}
          </div>
        </div>

        <div className="estimate-hub-card__meta">
          <div className="estimate-hub-card__updated">Updated: {updatedLabel}</div>
        </div>
      </button>

      {showArchivePill && (
        <button
          type="button"
          className="estimate-hub-card__archive"
          onClick={(event) => {
            event.stopPropagation();
            if (!archiving) {
              onArchive?.(estimate);
            }
          }}
          disabled={archiving}
          aria-label={`Archive ${estimate.title || "estimate"}`}
        >
          {archiving ? "Archiving…" : "Archive"}
        </button>
      )}
    </div>
  );
}

const ACTION_BTN = {
  padding: "10px 16px",
  borderRadius: "10px",
  border: "1px solid rgba(201,168,76,0.45)",
  background: "rgba(201,168,76,0.12)",
  color: "#c9a84c",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const CREATE_NEW_CLIENT_VALUE = "__create_new_client__";

const CREATE_NEW_CLIENT_OPTION = {
  value: CREATE_NEW_CLIENT_VALUE,
  label: "➕ Create New Client",
};

const STATUS_PILLS_WRAP_STYLE = HUB_STATUS_PILLS_WRAP_STYLE;
const STATUS_PILL_BTN_BASE_STYLE = HUB_STATUS_PILL_BTN_BASE_STYLE;
const STATUS_PILL_ACTIVE_GOLD_STYLE = HUB_STATUS_PILL_ACTIVE_GOLD_STYLE;

function estimateMatchesSearch(estimate, search) {
  if (!search) return true;

  return (
    estimate.title?.toLowerCase().includes(search) ||
    estimate.estimate_number?.toLowerCase().includes(search) ||
    estimate.clients?.client_name?.toLowerCase().includes(search)
  );
}

export function resolveEstimateHubInitialView({
  editEstimateId,
  initialView = "hub",
}) {
  if (editEstimateId) {
    return "summary";
  }
  if (initialView === "create") {
    return "builder";
  }
  return "hub";
}

export function EstimateHubScreen({
  onBack,
  currentUser,
  editEstimateId,
  initialStatusFilter,
  initialView = "hub",
  onCreateProjectFromEstimate,
  onOpenLinkedProject,
}) {
  const startNewEstimate = !editEstimateId && initialView === "create";
  const [view, setView] = useState(() =>
    resolveEstimateHubInitialView({ editEstimateId, initialView }),
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    initialStatusFilter ? resolveEstimateStatus(initialStatusFilter) : null,
  );
  const [estimates, setEstimates] = useState([]);
  const [clients, setClients] = useState([]);
  const [featureTypes, setFeatureTypes] = useState([]);
  const [workTypeTemplates, setWorkTypeTemplates] = useState([]);
  const [materialsCatalog, setMaterialsCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalogReady, setCatalogReady] = useState(false);

  const [activeEstimateId, setActiveEstimateId] = useState(editEstimateId || "");
  const [estimateTree, setEstimateTree] = useState(null);

  const [clientId, setClientId] = useState("");
  const [estimateNumber, setEstimateNumber] = useState(() =>
    startNewEstimate ? generateEstimateNumber() : "",
  );
  const [projectName, setProjectName] = useState("");
  const [status, setStatus] = useState("draft");
  const [activeLocationId, setActiveLocationId] = useState("");
  const [activeFeatureId, setActiveFeatureId] = useState("");
  const [builderError, setBuilderError] = useState("");
  const [pricingUnavailable, setPricingUnavailable] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [archiveConfirmEstimate, setArchiveConfirmEstimate] = useState(null);
  const [archivingEstimateId, setArchivingEstimateId] = useState(null);
  const [reportOptions, setReportOptions] = useState(DEFAULT_ESTIMATE_REPORT_OPTIONS);
  const [builderMode, setBuilderMode] = useState(() =>
    startNewEstimate ? ESTIMATE_BUILDER_MODES.EDIT : null,
  );
  const saveActionRef = useRef(null);
  const isDesktop = useIsDesktopViewport();
  const { can } = usePermissions();
  const canEditEstimate = can(CAPABILITY.ESTIMATE_EDIT);
  const canApproveEstimate = can(CAPABILITY.ESTIMATE_APPROVE);

  const pricingEditable =
    canEditEstimate && isEstimateEditable(status);
  const builderEditable =
    canEditEstimate && isEstimateBuilderEditable(status, builderMode);
  const isReviewMode = builderMode === ESTIMATE_BUILDER_MODES.REVIEW;

  const dismissSaveSuccess = useCallback(() => {
    setShowSaveSuccess(false);
  }, []);

  const reportBuilderFailure = useCallback((action, error) => {
    const message = formatSupabaseError(error);
    console.error(`[EstimateHub] ${action} failed:`, error);
    setBuilderError(message);
    return message;
  }, []);

  const reloadHub = useCallback(async () => {
    const rows = await loadEstimates(currentUser);
    setEstimates(rows);
  }, [currentUser]);

  const reloadClients = useCallback(async () => {
    const rows = await loadClientsForEstimates(currentUser);
    setClients(rows);
    return rows;
  }, [currentUser]);

  const handleClientCreatedFromEstimate = useCallback(
    async (client) => {
      await reloadClients();
      setClientId(client.client_id);
      setShowCreateClient(false);
    },
    [reloadClients],
  );

  function handleClientChange(value) {
    if (value === CREATE_NEW_CLIENT_VALUE) {
      setShowCreateClient(true);
      return;
    }

    setClientId(value);
  }

  const reloadBuilder = useCallback(async (estimateId) => {
    if (!estimateId) {
      return { success: false, errorMessage: "Missing estimate id." };
    }

    try {
      const tree = await loadEstimateTree(currentUser, estimateId);
      setEstimateTree(tree);
      setClientId(tree.client_id || "");
      setEstimateNumber(tree.estimate_number || "");
      setProjectName(tree.title || "");
      setStatus(resolveEstimateStatus(tree.status));
      setBuilderError("");
      return { success: true, errorMessage: "" };
    } catch (error) {
      const message = reportBuilderFailure("loadEstimateTree", error);
      return { success: false, errorMessage: message };
    }
  }, [currentUser, reportBuilderFailure]);

  const reloadWorkTypeTemplates = useCallback(async () => {
    try {
      const orgId = estimateTree?.organization_id ?? null;
      const rows = await loadWorkTypeTemplates(orgId);
      setWorkTypeTemplates(rows);
    } catch (error) {
      console.error("[EstimateHub] loadWorkTypeTemplates failed:", error);
    }
  }, [estimateTree?.organization_id]);

  useEffect(() => {
    if (estimateTree?.estimate_id) {
      reloadWorkTypeTemplates();
    }
  }, [estimateTree?.estimate_id, estimateTree?.organization_id, reloadWorkTypeTemplates]);

  useEffect(() => {
    if (initialStatusFilter) {
      setStatusFilter(resolveEstimateStatus(initialStatusFilter));
      setSearchTerm("");
    } else if (initialStatusFilter === null) {
      // no-op: leaving existing selection as-is unless explicitly cleared by user
    }
  }, [initialStatusFilter]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      setLoading(true);
      if (shouldSkipSupabase(currentUser)) {
        setCatalogReady(true);
        setEstimates([]);
        setClients([]);
        setFeatureTypes([]);
        setMaterialsCatalog([]);
        setWorkTypeTemplates([]);
        setLoading(false);
        return;
      }

      try {
        await ensureMvpEstimateCatalog();
        if (cancelled) return;
        setCatalogReady(true);

        const [estimateRows, clientRows, catalogRows, materialRows, templateRows] =
          await Promise.all([
            loadEstimates(currentUser),
            loadClientsForEstimates(currentUser),
            loadFeatureTypesCatalog(),
            loadMaterialsCatalog(currentUser),
            loadWorkTypeTemplates(),
          ]);

        if (cancelled) return;
        setEstimates(estimateRows);
        setClients(clientRows);
        setFeatureTypes(catalogRows);
        setMaterialsCatalog(materialRows);
        setWorkTypeTemplates(templateRows);

        if (editEstimateId) {
          setActiveEstimateId(editEstimateId);
          setView("summary");
          await reloadBuilder(editEstimateId);
        }
      } catch (error) {
        console.error("[EstimateHub] Bootstrap failed:", error);
        if (!cancelled) {
          alert(`Unable to load estimate module.\n\n${formatSupabaseError(error)}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [currentUser, editEstimateId, reloadBuilder]);

  function beginNewEstimate() {
    setActiveEstimateId("");
    setClientId("");
    setEstimateNumber(generateEstimateNumber());
    setProjectName("");
    setStatus("draft");
    setActiveLocationId("");
    setActiveFeatureId("");
    setBuilderMode(ESTIMATE_BUILDER_MODES.EDIT);
    setView("builder");
  }

  async function handleCreateEstimate() {
    if (blockDemoSave(currentUser)) {
      return;
    }

    if (!clientId) {
      alert("Select a client.");
      return;
    }
    if (!projectName.trim()) {
      alert("Enter a project name.");
      return;
    }

    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const number = estimateNumber.trim() || generateEstimateNumber();
      const estimateId = await createEstimate(currentUser, {
        clientId,
        estimateNumber: number,
        title: projectName.trim(),
        userId: user?.id,
      });

      setActiveEstimateId(estimateId);
      setEstimateNumber(number);
      setActiveLocationId("");
      setActiveFeatureId("");
      setBuilderMode(ESTIMATE_BUILDER_MODES.EDIT);
      setView("builder");
      await reloadHub();
      await reloadBuilder(estimateId);
    } catch (error) {
      const message = reportBuilderFailure("createEstimate", error);
      // Diagnostic code + bare DB error code (e.g. "23503") — not the
      // message/details/hint/SQL — so a failure can be reported accurately
      // without needing desktop devtools. See EST_CREATE_* in estimates.js.
      let reference = "";
      if (ESTIMATE_CREATE_DIAGNOSTIC_CODES.has(error?.code)) {
        reference = `\n\nReference: ${error.code}`;
        if (error?.dbCode) {
          reference += `\nDB Code: ${error.dbCode}`;
        }
      }
      alert(`Unable to create estimate.\n\n${message}${reference}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleOpenEstimate(estimateId) {
    setActiveEstimateId(estimateId);
    setActiveLocationId("");
    setActiveFeatureId("");
    setView("summary");
    setBuilderError("");
    const result = await reloadBuilder(estimateId);
    if (!result.success) {
      alert(`Unable to open estimate.\n\n${result.errorMessage}`);
    }
  }

  async function handleSavePricing(pricing) {
    if (!activeEstimateId || !pricingEditable) return;

    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      await updateEstimatePricing(currentUser, activeEstimateId, {
        ...pricing,
        userId: user?.id,
      });

      setPricingUnavailable(false);
      await reloadHub();
      await reloadBuilder(activeEstimateId);
    } catch (error) {
      console.error("[EstimateHub] Save pricing failed:", error);
      const message = formatSupabaseError(error);
      if (/waste_adjustment|final_estimate_price|tax_line_1_type/i.test(message)) {
        setPricingUnavailable(true);
      }
      alert(`Unable to save pricing.\n\n${message}`);
    } finally {
      setSaving(false);
    }
  }

  function handleEditEstimateFromSummary() {
    setBuilderMode(ESTIMATE_BUILDER_MODES.EDIT);
    setActiveLocationId("");
    setActiveFeatureId("");
    setView("builder");
    setBuilderError("");
  }

  function handleReviewEstimateFromSummary() {
    setBuilderMode(ESTIMATE_BUILDER_MODES.REVIEW);
    setActiveLocationId("");
    setActiveFeatureId("");
    setView("builder");
    setBuilderError("");
  }

  function handleUpdateEstimateFromSummary() {
    setBuilderMode(ESTIMATE_BUILDER_MODES.UPDATE);
    setActiveLocationId("");
    setActiveFeatureId("");
    setView("builder");
    setBuilderError("");
  }

  function handleOpenLinkedProjectFromSummary() {
    if (!estimateTree?.project_id) return;

    onOpenLinkedProject?.({
      projectId: estimateTree.project_id,
      projectName: estimateTree.title || projectName.trim() || "",
    });
  }

  async function handleStatusChange(nextStatus) {
    if (!activeEstimateId) return;
    if (nextStatus === "converted" || nextStatus === "archived") return;
    if (nextStatus === "approved" && !canApproveEstimate) return;
    if (!canEditEstimate && nextStatus !== "approved") return;

    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      await updateEstimateStatus(currentUser, activeEstimateId, nextStatus, user?.id);
      setStatus(resolveEstimateStatus(nextStatus));
      await reloadHub();
      const reloadResult = await reloadBuilder(activeEstimateId);
      if (!reloadResult.success) {
        throw new Error(reloadResult.errorMessage);
      }
    } catch (error) {
      const message = reportBuilderFailure("updateEstimateStatus", error);
      alert(`Unable to update status.\n\n${message}`);
      throw error;
    } finally {
      setSaving(false);
    }
  }

  async function handleArchiveEstimate(estimateId = activeEstimateId) {
    if (!estimateId) return;

    setSaving(true);
    setArchivingEstimateId(estimateId);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      await archiveEstimate(currentUser, estimateId, user?.id);

      if (estimateId === activeEstimateId) {
        setStatus("archived");
        const reloadResult = await reloadBuilder(estimateId);
        if (!reloadResult.success) {
          throw new Error(reloadResult.errorMessage);
        }
      }

      await reloadHub();
      setArchiveConfirmEstimate(null);
    } catch (error) {
      const message = reportBuilderFailure("archiveEstimate", error);
      alert(`Unable to archive estimate.\n\n${message}`);
      throw error;
    } finally {
      setSaving(false);
      setArchivingEstimateId(null);
    }
  }

  function handleRequestHubArchive(estimate) {
    setArchiveConfirmEstimate({
      estimateId: estimate.estimate_id,
      title: estimate.title || "Untitled Estimate",
    });
  }

  async function handleConfirmHubArchive() {
    if (!archiveConfirmEstimate?.estimateId || archivingEstimateId) return;

    try {
      await handleArchiveEstimate(archiveConfirmEstimate.estimateId);
    } catch {
      // Error alert handled in handleArchiveEstimate.
    }
  }

  function handleBackFromBuilder() {
    setBuilderMode(null);
    setActiveLocationId("");
    setActiveFeatureId("");

    if (!activeEstimateId) {
      setView("hub");
      return;
    }

    setView("summary");
    reloadBuilder(activeEstimateId);
  }

  function handleBackFromSummary() {
    setView("hub");
    reloadHub();
  }

  const handleHardwareBack = useCallback(() => {
    if (view === "report-preview") {
      setView("summary");
      return true;
    }

    if (view === "report-options") {
      setView("summary");
      return true;
    }

    if (view === "builder") {
      if (activeFeatureId) {
        setActiveFeatureId("");
        return true;
      }
      if (activeLocationId) {
        setActiveLocationId("");
        return true;
      }
      handleBackFromBuilder();
      return true;
    }

    if (view === "summary") {
      handleBackFromSummary();
      return true;
    }

    if (typeof onBack === "function") {
      onBack();
      return true;
    }
    return false;
  }, [onBack, view, activeFeatureId, activeLocationId]);

  useHardwareBackHandler(handleHardwareBack);

  function handleGenerateReportFromSummary() {
    setReportOptions({ ...DEFAULT_ESTIMATE_REPORT_OPTIONS });
    setView("report-options");
  }

  function handlePreviewReportFromSummary() {
    setReportOptions({ ...DEFAULT_ESTIMATE_REPORT_OPTIONS });
    setView("report-preview");
  }

  function handleOpenReportPreview(options) {
    setReportOptions(options);
    setView("report-preview");
  }

  const reportClientName =
    estimateTree?.clients?.client_name ||
    clients.find((client) => client.client_id === clientId)?.client_name ||
    "—";

  async function handleSaveHeader() {
    if (!activeEstimateId || !builderEditable) return;

    if (!clientId) {
      alert("Select a client.");
      return;
    }
    if (!projectName.trim()) {
      alert("Enter a project name.");
      return;
    }
    if (!estimateNumber.trim()) {
      alert("Enter an estimate name / number.");
      return;
    }

    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      await updateEstimateHeader(currentUser, activeEstimateId, {
        clientId,
        estimateNumber: estimateNumber.trim(),
        title: projectName.trim(),
        userId: user?.id,
      });

      await reloadHub();
      const reloadResult = await reloadBuilder(activeEstimateId);
      if (!reloadResult.success) {
        throw new Error(reloadResult.errorMessage);
      }
      setShowSaveSuccess(true);
      requestAnimationFrame(() => {
        saveActionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    } catch (error) {
      const message = reportBuilderFailure("saveEstimate", error);
      alert(`Unable to save estimate.\n\n${message}`);
    } finally {
      setSaving(false);
    }
  }

  function handleCreateProjectFromEstimate() {
    if (!activeEstimateId) return;
    if (estimateTree?.project_id) return;
    if (resolveEstimateStatus(status) !== "approved") return;

    const estimateClientName =
      estimateTree?.clients?.client_name ||
      clients.find((c) => c.client_id === clientId)?.client_name ||
      "";

    onCreateProjectFromEstimate?.({
      estimateId: activeEstimateId,
      projectName: projectName.trim() || estimateTree?.title || "",
      clientId,
      clientName: estimateClientName,
    });
  }

  if (loading) {
    return (
      <CrewPilotModuleShell
        title="Estimates"
        onBack={onBack}
        layout={PAGE_LAYOUT.COMMAND}
        primary
      >
        <div style={{ opacity: 0.75 }}>Loading…</div>
      </CrewPilotModuleShell>
    );
  }

  if (showCreateClient) {
    return (
      <CreateClientScreen
        embedFromEstimate
        onClientCreated={handleClientCreatedFromEstimate}
        onCancel={() => setShowCreateClient(false)}
      />
    );
  }

  if (view === "hub") {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = estimates.filter((row) => {
      const rowStatus = resolveEstimateStatus(row.status);
      if (statusFilter) {
        if (rowStatus !== statusFilter) return false;
      } else if (!ESTIMATE_ACTIVE_HUB_STATUSES.includes(rowStatus)) {
        return false;
      }
      return estimateMatchesSearch(row, normalizedSearch);
    });

    const archiveModal = (
        archiveConfirmEstimate && (
          <ViewportPortal>
          <div
            style={MODAL_OVERLAY_STYLE}
            onClick={() =>
              !archivingEstimateId && setArchiveConfirmEstimate(null)
            }
          >
            <div
              style={MODAL_PANEL_STYLE}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="hub-archive-estimate-title"
            >
              <h2
                id="hub-archive-estimate-title"
                style={{ marginTop: 0, marginBottom: "12px", fontSize: "20px" }}
              >
                Archive Estimate?
              </h2>
              <p style={{ opacity: 0.85, lineHeight: 1.5, marginBottom: "8px" }}>
                This estimate will be removed from active estimate lists and moved
                to Archived estimates.
              </p>
              {archiveConfirmEstimate.title && (
                <p
                  style={{
                    opacity: 0.65,
                    fontSize: "14px",
                    marginBottom: "20px",
                  }}
                >
                  {archiveConfirmEstimate.title}
                </p>
              )}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  className="nx-btn"
                  onClick={() => setArchiveConfirmEstimate(null)}
                  disabled={Boolean(archivingEstimateId)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="nx-btn nx-btn-gold"
                  onClick={handleConfirmHubArchive}
                  disabled={Boolean(archivingEstimateId)}
                >
                  {archivingEstimateId ? "Archiving…" : "Archive"}
                </button>
              </div>
            </div>
          </div>
          </ViewportPortal>
        )
    );

    if (isDesktop) {
      return (
        <EstimateHubDesktop
          onBack={onBack}
          catalogReady={catalogReady}
          estimates={estimates}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          searchTerm={searchTerm}
          onSearch={setSearchTerm}
          filtered={filtered}
          onNew={beginNewEstimate}
          onOpen={handleOpenEstimate}
          onArchive={handleRequestHubArchive}
          archivingEstimateId={archivingEstimateId}
        >
          {archiveModal}
        </EstimateHubDesktop>
      );
    }

    const showNewEstimate =
      canEditEstimate || can(CAPABILITY.SCREEN_ESTIMATE_HUB);

    return (
      <CrewPilotModuleShell
        title="Estimates"
        subtitle="Create and manage job estimates."
        onBack={onBack}
        layout={PAGE_LAYOUT.COMMAND}
        primary
        compact
      >
        <div className="estimate-hub">
        {!catalogReady && (
          <div style={{ opacity: 0.75, marginBottom: "12px" }}>
            Catalog unavailable.
          </div>
        )}

        {showNewEstimate ? (
          <div className="hub-primary-slot">
            <HubPrimaryAction
              onClick={beginNewEstimate}
              label="New Estimate"
              ariaLabel="+ New Estimate"
            />
          </div>
        ) : null}

        <div
          className="estimate-hub__filters"
          style={STATUS_PILLS_WRAP_STYLE}
          aria-label="Estimate status filters"
        >
          <button
            type="button"
            className={`estimate-hub__filter--all${
              statusFilter === null ? " is-active" : ""
            }`}
            onClick={() => setStatusFilter(null)}
            style={{
              ...STATUS_PILL_BTN_BASE_STYLE,
              ...(statusFilter === null ? STATUS_PILL_ACTIVE_GOLD_STYLE : {}),
            }}
          >
            All
          </button>
          {ESTIMATE_STATUS_OPTIONS.map((opt) => {
            const isActive = statusFilter === opt.value;
            const pill = getEstimateStatusPillStyle(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() =>
                  setStatusFilter((prev) => (prev === opt.value ? null : opt.value))
                }
                style={{
                  ...STATUS_PILL_BTN_BASE_STYLE,
                  border: isActive ? pill.border : STATUS_PILL_BTN_BASE_STYLE.border,
                  background: isActive ? pill.background : STATUS_PILL_BTN_BASE_STYLE.background,
                  color: isActive ? pill.color : STATUS_PILL_BTN_BASE_STYLE.color,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div className="estimate-hub__search">
          <svg
            className="estimate-hub__search-icon"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path
              d="M10.5 10.5L14 14"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="search"
            className="estimate-hub__search-input"
            placeholder="Search estimates…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search estimates"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="estimate-hub__empty">No estimates yet.</div>
        ) : (
          <div className="estimate-hub__list">
            {filtered.map((estimate) => (
              <EstimateHubCard
                key={estimate.estimate_id}
                estimate={estimate}
                onOpen={handleOpenEstimate}
                onArchive={handleRequestHubArchive}
                archiving={archivingEstimateId === estimate.estimate_id}
              />
            ))}
          </div>
        )}
        </div>

        {archiveModal}
      </CrewPilotModuleShell>
    );
  }

  if (view === "report-options" && activeEstimateId && estimateTree) {
    return (
      <EstimateReportOptionsScreen
        estimateTitle={estimateTree.title}
        estimateNumber={estimateTree.estimate_number}
        options={reportOptions}
        onChange={setReportOptions}
        onPreview={handleOpenReportPreview}
        onBack={() => setView("summary")}
      />
    );
  }

  if (view === "report-preview" && activeEstimateId && estimateTree) {
    return (
      <EstimateReportPreviewScreen
        estimateTree={estimateTree}
        materialsCatalog={materialsCatalog}
        clientName={reportClientName}
        statusLabel={formatEstimateStatus(status)}
        options={reportOptions}
        onBack={() => setView("summary")}
        onEditOptions={() => setView("report-options")}
      />
    );
  }

  if (view === "summary" && activeEstimateId && estimateTree) {
    return (
      <EstimateSummaryScreen
        estimateTree={estimateTree}
        materialsCatalog={materialsCatalog}
        editable={pricingEditable}
        saving={saving}
        pricingSchemaMissing={pricingUnavailable}
        linkedProjectId={estimateTree.project_id || null}
        onBack={handleBackFromSummary}
        onEditEstimate={handleEditEstimateFromSummary}
        onReviewEstimate={handleReviewEstimateFromSummary}
        onUpdateEstimate={handleUpdateEstimateFromSummary}
        onCreateProject={handleCreateProjectFromEstimate}
        onOpenProject={handleOpenLinkedProjectFromSummary}
        onSavePricing={handleSavePricing}
        onStatusChange={handleStatusChange}
        onArchiveEstimate={() => handleArchiveEstimate(activeEstimateId)}
        onGenerateReport={handleGenerateReportFromSummary}
        onPreviewReport={handlePreviewReportFromSummary}
      />
    );
  }

  if (view === "summary" && activeEstimateId && !estimateTree) {
    return (
      <CrewPilotModuleShell
        title="Estimate Summary"
        sectionLabel="Estimate Summary"
        onBack={handleBackFromSummary}
        layout={PAGE_LAYOUT.HUB}
      >
        <div style={{ opacity: 0.75 }}>
          {builderError || "Loading estimate…"}
        </div>
      </CrewPilotModuleShell>
    );
  }

  if (view === "builder" && isReviewMode && activeEstimateId && estimateTree) {
    const reviewClientName =
      estimateTree.clients?.client_name ||
      clients.find((client) => client.client_id === clientId)?.client_name ||
      "—";

    return (
      <CrewPilotModuleShell
        title={getBuilderShellTitle(builderMode, false)}
        subtitle={estimateNumber || estimateTree.estimate_number || ""}
        sectionLabel="Estimate Review"
        onBack={handleBackFromBuilder}
        desktopBackLabel="Estimate Summary"
        layout={isDesktop ? PAGE_LAYOUT.COMMAND : PAGE_LAYOUT.HUB}
        compact={isDesktop}
      >
        {builderError && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px 14px",
              borderRadius: "10px",
              border: "1px solid rgba(224,107,90,0.45)",
              background: "rgba(224,107,90,0.12)",
              color: "#e06b5a",
              fontSize: "14px",
            }}
          >
            {builderError}
          </div>
        )}

        <EstimateReviewView
          estimateTree={estimateTree}
          materialsCatalog={materialsCatalog}
          clientName={reviewClientName}
          estimateNumber={estimateNumber || estimateTree.estimate_number}
          projectName={projectName || estimateTree.title}
          status={status}
        />
      </CrewPilotModuleShell>
    );
  }

  const isNewEstimate = !activeEstimateId;
  const builderTitle = getBuilderShellTitle(builderMode, isNewEstimate);

  if (isNewEstimate && isDesktop) {
    return (
      <CrewPilotModuleShell
        title="New Estimate"
        subtitle={estimateNumber || "Draft"}
        onBack={handleBackFromBuilder}
        desktopBackLabel="Estimates"
        layout={PAGE_LAYOUT.FORM}
        compact
      >
        {builderError ? <div className="est-inline-error">{builderError}</div> : null}
        <DeskPanel title="Estimate Information">
          <p className="dp-hint">* Indicates required information</p>
          <div className="est-d-grid-2">
            <CPDropdown
              label="Client"
              value={clientId}
              onChange={handleClientChange}
              options={[
                CREATE_NEW_CLIENT_OPTION,
                ...clients.map((client) => ({
                  value: client.client_id,
                  label: client.client_name,
                })),
              ]}
              placeholder="Select client"
            />
            <CPInput
              label="Estimate Name"
              value={estimateNumber}
              onChange={setEstimateNumber}
              placeholder="EST-20260619-1234"
              required
            />
            <CPInput
              label="Project Name"
              value={projectName}
              onChange={setProjectName}
              placeholder="Smith Primary Bath Renovation"
              required
            />
            <div className="cp-input-group">
              <label className="cp-input-label">Status</label>
              <div className="estimate-create-page__status">{formatEstimateStatus(status)}</div>
            </div>
          </div>
          <div className="est-d-form-actions" ref={saveActionRef}>
            <button type="button" className="dt-btn" onClick={handleBackFromBuilder} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="dt-btn dt-btn--primary"
              onClick={handleCreateEstimate}
              disabled={saving}
            >
              {saving ? "Creating…" : "Create Estimate"}
            </button>
          </div>
        </DeskPanel>
      </CrewPilotModuleShell>
    );
  }

  if (isNewEstimate) {
    return (
      <div className="estimate-create-page screen nx-screen">
        <header className="estimate-create-page__top">
          <button
            type="button"
            className="cockpit-back-btn estimate-create-page__back"
            onClick={handleBackFromBuilder}
          >
            ← Back
          </button>
          <h1 className="estimate-create-page__title">New Estimate</h1>
          <p className="estimate-create-page__number">
            {estimateNumber || "Draft"}
          </p>
          <p className="estimate-create-page__lede">
            Create a new estimate and then begin building the estimate
            workspace.
          </p>
        </header>

        {builderError ? (
          <div className="estimate-create-page__error">{builderError}</div>
        ) : null}

        <section
          className="estimate-create-page__section"
          aria-labelledby="estimate-information-heading"
        >
          <h2
            id="estimate-information-heading"
            className="estimate-create-page__section-title"
          >
            Estimate Information
          </h2>
          <hr className="estimate-create-page__section-rule" />
          <p className="estimate-create-page__required">
            * Indicates required information
          </p>

          <div className="estimate-create-page__grid">
            <div className="estimate-create-page__field">
              <CPDropdown
                label="Client"
                value={clientId}
                onChange={handleClientChange}
                options={[
                  CREATE_NEW_CLIENT_OPTION,
                  ...clients.map((client) => ({
                    value: client.client_id,
                    label: client.client_name,
                  })),
                ]}
                placeholder="Select client"
              />
            </div>

            <div className="estimate-create-page__field">
              <CPInput
                label="Estimate Name"
                value={estimateNumber}
                onChange={setEstimateNumber}
                placeholder="EST-20260619-1234"
                required
              />
            </div>

            <div className="estimate-create-page__field">
              <CPInput
                label="Project Name"
                value={projectName}
                onChange={setProjectName}
                placeholder="Smith Primary Bath Renovation"
                required
              />
            </div>

            <div className="estimate-create-page__field">
              <div className="cp-input-group">
                <label className="cp-input-label">Status</label>
                <div className="estimate-create-page__status">
                  {formatEstimateStatus(status)}
                </div>
              </div>
            </div>
          </div>

          <div className="estimate-create-page__actions" ref={saveActionRef}>
            <button
              type="button"
              style={ACTION_BTN}
              onClick={handleCreateEstimate}
              disabled={saving}
            >
              {saving ? "Creating…" : "Create Estimate"}
            </button>
          </div>
        </section>

        <section
          className="estimate-create-page__workspace-region"
          aria-label="Estimate workspace"
        >
          <p className="estimate-create-page__workspace-label">
            Estimate Workspace
          </p>
          <div className="estimate-create-page__workspace-slot" />
        </section>
      </div>
    );
  }

  if (!estimateTree) {
    return (
      <CrewPilotModuleShell
        title={builderTitle}
        subtitle={estimateNumber || "Draft estimate builder"}
        sectionLabel="Estimate Workspace"
        onBack={handleBackFromBuilder}
      >
        {builderError && <div className="est-inline-error">{builderError}</div>}
      </CrewPilotModuleShell>
    );
  }

  // Desktop "Edit Estimate": one single-screen editor instead of Location → Feature pages.
  // Phones (and Update/Review modes) keep the step-by-step screens below.
  if (isDesktop && builderMode === ESTIMATE_BUILDER_MODES.EDIT) {
    return (
      <DesktopEstimateEditor
        estimateTree={estimateTree}
        materialsCatalog={materialsCatalog}
        featureTypes={featureTypes}
        workTypeTemplates={workTypeTemplates}
        organizationId={estimateTree?.organization_id ?? null}
        clientName={
          estimateTree?.clients?.client_name ||
          clients.find((c) => c.client_id === clientId)?.client_name ||
          ""
        }
        estimateNumber={estimateNumber}
        projectName={projectName}
        editable={builderEditable}
        onReload={() => reloadBuilder(activeEstimateId)}
        onBack={handleBackFromBuilder}
        onOpenPricing={handleBackFromBuilder}
      />
    );
  }

  const activeLocation = activeLocationId
    ? (estimateTree.locations || []).find((l) => l.room_id === activeLocationId)
    : null;

  const activeFeature =
    activeLocation && activeFeatureId
      ? (activeLocation.workTypes || []).find(
          (wt) => wt.estimate_feature_id === activeFeatureId,
        )
      : null;

  if (activeLocation && activeFeature) {
    return (
      <FeatureWorkspaceScreen
        workType={activeFeature}
        materialsCatalog={materialsCatalog}
        editable={builderEditable}
        organizationId={estimateTree?.organization_id ?? null}
        onReload={() => reloadBuilder(activeEstimateId)}
        onTemplatesChanged={reloadWorkTypeTemplates}
        onBack={() => setActiveFeatureId("")}
        parentLabel={formatLocationLabel(activeLocation)}
        onFeatureDeleted={async () => {
          setActiveFeatureId("");
          await reloadBuilder(activeEstimateId);
        }}
      />
    );
  }

  if (activeLocation) {
    return (
      <LocationWorkspaceScreen
        location={activeLocation}
        featureTypes={featureTypes}
        workTypeTemplates={workTypeTemplates}
        materialsCatalog={materialsCatalog}
        organizationId={estimateTree?.organization_id ?? null}
        editable={builderEditable}
        onReload={() => reloadBuilder(activeEstimateId)}
        onTemplatesChanged={reloadWorkTypeTemplates}
        onBack={() => setActiveLocationId("")}
        parentLabel={projectName || estimateTree?.title || undefined}
        onOpenFeature={(estimateFeatureId) => setActiveFeatureId(estimateFeatureId)}
        onLocationDeleted={async () => {
          setActiveLocationId("");
          await reloadBuilder(activeEstimateId);
        }}
      />
    );
  }

  return (
    <EstimateWorkspaceScreen
      estimateTree={estimateTree}
      materialsCatalog={materialsCatalog}
      clients={clients}
      clientId={clientId}
      estimateNumber={estimateNumber}
      projectName={projectName}
      status={status}
      editable={builderEditable}
      saving={saving}
      builderError={builderError}
      showSaveSuccess={showSaveSuccess}
      onDismissSaveSuccess={dismissSaveSuccess}
      onClientChange={handleClientChange}
      onEstimateNumberChange={setEstimateNumber}
      onProjectNameChange={setProjectName}
      onSaveHeader={handleSaveHeader}
      onBack={handleBackFromBuilder}
      onOpenLocation={(roomId) => setActiveLocationId(roomId)}
      onAddLocation={() => reloadBuilder(activeEstimateId)}
      onOpenPricing={handleBackFromBuilder}
    />
  );
}
